// 事件詳細（plan/vms-web-v0.md EVT-04）：前後片段播放、關聯攝影機同時刻、同 cycle 的 started / ended、AI 分析結果。
// 事件不是案件（VMS §6）：「建立案件」只在產業應用啟用且有 case 權限時出現，v0 web 沒有這個入口。
// 事件內容從列表帶進來（router state），重新整理時從 sessionStorage 取回；都沒有（新分頁、通知、推播、email、書籤）
// 就用網址的家戶 + 事件 ID 向後端查（lib/eventLookup.ts，P0-2）；真的查不到才說找不到。範圍外的家戶照樣能看，頁頂提示可切範圍。
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';
import Hls from 'hls.js';
import { lensOf, vms, type VmsFacadeEvent } from '@kinio/web-sdk';
import { LensChip } from '../../components/LensChip';
import { useDevicesForHome } from '../../lib/useDevices';
import { useScope } from '../../lib/scope';
import { lookupEvent } from '../../lib/eventLookup';
import { OutOfScopeHint } from '../../components/OutOfScopeHint';
import { CHILD_SEP, can, eventDeviceLabel, fullName, type VmsDevice } from '../../lib/devices';
import { linkApi, searchApi, type SearchDoc } from '../../lib/planeApi';
import { formatDateTime, formatTime } from '../../lib/tz';
import { useTzResolver } from '../../lib/useTz';
import { deviceHref } from '../devices/DeviceDetailPage';
import { classify } from './EventsPage';
import { AlarmActions } from '../../components/AlarmActions';
import { AlarmHistoryList } from '../../components/AlarmHistory';
import { useApp } from '../../lib/AppContext';
import { useAlarmStates } from '../../lib/alarmStates';
import { alarmApi } from '../../lib/alarms';
import { eventName } from '../../lib/eventName';
import { exportHref, playbackHref } from '../../lib/links';
import { clipSuspect, fragsSuspect } from './clipGuard';
import { isLocalRecorder } from '../playback/view';
import { PLAY_FALLBACK_MS } from '../playback/sourcePick';
import { cloudClipIn } from '../../lib/facade';
import { useThumb } from '../../lib/thumbs';
import { idVariants } from '../../lib/eventLookup';
import { episodeState, pairEpisodes, phaseOf, type EpItem, type Episode } from '../../lib/eventEpisodes';
import { durationText } from '../../lib/duration';

export interface EventView {
  id: string;
  homeId: string;
  aid: string;
  key: string;
  cls?: string | null;
  /** started / ended（單次 = undefined）；讀事件本身，不靠清單帶進來（第八輪 B8-01） */
  phase?: string;
  /** 事件原本的 device_ref（通道事件可能是主機＋payload.ch；搜尋用這個）與綁定 id（同一次的配對用） */
  ref?: string;
  binding?: string;
  occurredAt: string;
  fields: Record<string, unknown>;
  aiTags: string[];
  hasClip: boolean;
  /** 多鏡頭：事件的鏡頭（0 = 主鏡頭；舊的 sessionStorage 紀錄沒有 = 0）。 */
  lens?: number;
}

export function fromFacade(homeId: string, e: VmsFacadeEvent): EventView {
  return {
    id: e.event_id,
    homeId,
    // device_ref 一律是完整字串（通道 = <parent>:chN，SPEC #terms），channel 只是另給的編號
    aid: e.thing?.device_ref ?? '',
    key: e.key,
    cls: e.class,
    phase: phaseOf(e),
    ref: e.thing?.device_ref ?? '',
    binding: bindingOf(e),
    occurredAt: e.occurred_at,
    fields: e.fields ?? {},
    // 單筆事件端點另帶 ai_tags / has_clip（façade 列表沒有）
    aiTags: (e as { ai_tags?: string[] }).ai_tags ?? [],
    hasClip: e.media_state === 'ready' || !!(e as { has_clip?: boolean }).has_clip,
    lens: lensOf(e.thing),
  };
}

function bindingOf(e: VmsFacadeEvent): string | undefined {
  const b = (e.scope as { binding_id?: unknown } | undefined)?.binding_id;
  return typeof b === 'string' && b ? b : undefined;
}

export function fromDoc(d: SearchDoc): EventView {
  const ch = d.payload?.ch;
  return {
    id: d.event_id,
    homeId: d.home_id,
    aid: typeof ch === 'number' && !d.device_ref.includes(CHILD_SEP) ? `${d.device_ref}${CHILD_SEP}${ch}` : d.device_ref,
    key: d.key,
    cls: d.class,
    // 搜尋列的 phase 在最上層（search.event_docs.phase），payload 裡不一定有（第八輪 B8-01：原本只看 payload，整段配錯）
    phase: phaseOf(d),
    ref: d.device_ref,
    binding: d.binding_id || undefined,
    occurredAt: d.occurred_at,
    fields: d.payload ?? {},
    aiTags: d.ai_tags ?? [],
    hasClip: d.has_clip,
    lens: lensOf(d.payload),
  };
}

const STORE_KEY = 'kinio.vms.eventDetail.';

function remember(ev: EventView) {
  try {
    sessionStorage.setItem(STORE_KEY + ev.id, JSON.stringify(ev));
  } catch {
    /* 私密視窗等：重新整理就回列表 */
  }
}

function recall(id: string): EventView | null {
  try {
    const s = sessionStorage.getItem(STORE_KEY + id);
    return s ? (JSON.parse(s) as EventView) : null;
  } catch {
    return null;
  }
}

export function EventDetailPage() {
  const { t, i18n } = useTranslation();
  const { homeId = '', eventId = '' } = useParams();
  const loc = useLocation();
  const st = loc.state as { ev?: VmsFacadeEvent; doc?: SearchDoc } | null;
  const local: EventView | null = st?.ev ? fromFacade(homeId, st.ev) : st?.doc ? fromDoc(st.doc) : recall(eventId);
  const { homes, loading: scopeLoading } = useScope();
  const api = homes.find((h) => h.home_id === homeId)?.endpoints.api;
  const found = useQuery({
    queryKey: ['event-lookup', homeId, eventId],
    queryFn: async () => {
      const r = await lookupEvent(api!, homeId, eventId);
      return r ? ('doc' in r ? fromDoc(r.doc) : fromFacade(homeId, r.ev)) : null;
    },
    enabled: !local && !!api && !!eventId,
    staleTime: 60_000,
    retry: false,
  });
  const ev = local ?? found.data ?? null;
  useEffect(() => {
    if (ev) remember(ev);
  }, [ev?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const { tree, outOfScope, loading: devLoading } = useDevicesForHome(homeId, ev?.aid);
  const tzOf = useTzResolver();
  const all = tree.flatMap((d) => [d, ...d.children]);
  const dev = ev ? (all.find((d) => d.homeId === ev.homeId && d.aid === ev.aid) ?? all.find((d) => d.homeId === ev.homeId && d.aid === ev.aid.split(CHILD_SEP)[0])) : undefined;
  if (!ev) {
    const searching = scopeLoading || found.isLoading;
    return (
      <section data-event-detail-missing={eventId}>
        <p>
          <Link to="/events">← {t('evd.back')}</Link>
        </p>
        {searching ? (
          <div className="card skeleton-block" aria-busy="true" data-testid="evd-loading">
            <p className="muted">{t('evd.looking')}</p>
          </div>
        ) : (
          <div className="card" data-testid="evd-not-found">
            <p>{t('evd.not_found')}</p>
            <Link className="btn" to="/events">
              {t('evd.back_list')}
            </Link>
          </div>
        )}
      </section>
    );
  }
  const ts = Date.parse(ev.occurredAt) / 1000;
  const cls = classify(ev.key, ev.cls ?? undefined);
  const ai = ev.fields.cloud_ai as { verdict?: string; labels?: string[]; caption?: string; confidence?: number } | undefined;
  const cycle = typeof ev.fields.cycle_id === 'string' ? (ev.fields.cycle_id as string) : typeof ev.fields.id === 'string' ? (ev.fields.id as string) : '';
  return (
    <section data-event-detail={ev.id}>
      <p>
        <Link to="/events">← {t('evd.back')}</Link>
      </p>
      {outOfScope && <OutOfScopeHint homeId={ev.homeId} kind="event" />}
      <h1 className="page-title">{eventName(t, ev.key, ev.cls, ev.fields, ev.ref || ev.aid)}</h1>
      <table className="grid dinfo">
        <tbody>
          <tr>
            <th scope="row">{t('evd.time')}</th>
            <td>{formatDateTime(ts, tzOf(ev.homeId), i18n.language, true)}</td>
          </tr>
          <tr>
            <th scope="row">{t('evd.class')}</th>
            <td>
              {t(`events.class_${cls}`)}
              {ev.phase && ev.phase !== 'instant' && <span className="badge" style={{ marginLeft: 6 }}>{t(`evd.phase_${ev.phase}`, { defaultValue: ev.phase })}</span>}
            </td>
          </tr>
          <tr>
            <th scope="row">{t('evd.device')}</th>
            {/* 設備清單還沒載入前寫「載入中…」，查不到才寫「不在清單裡的設備」，不露原始 ID（第九輪 B9-07） */}
            <td data-testid="evd-device">
              {dev ? (
                <>
                  <Link to={deviceHref(dev)}>{fullName(dev, tree)}</Link>
                  <LensChip cam={dev} lens={ev.lens ?? 0} />
                </>
              ) : devLoading ? (
                <span className="muted">{t('common.loading')}</span>
              ) : (
                eventDeviceLabel(t, (ref) => all.find((d) => d.homeId === ev.homeId && d.aid === ref), tree, ev.aid)
              )}
            </td>
          </tr>
          {(ai || ev.aiTags.length > 0) && (
            <tr>
              <th scope="row">{t('evd.ai')}</th>
              <td data-testid="evd-ai">
                {ai?.verdict && <span className="badge">{t(`evd.verdict_${ai.verdict}`, { defaultValue: ai.verdict })}</span>}
                {(ai?.labels ?? []).map((l) => (
                  <span key={l} className="badge" style={{ marginLeft: 6 }}>
                    {t(`aiCloud.label_${l}`, { defaultValue: l })}
                  </span>
                ))}
                {ev.aiTags
                  .filter((x) => !x.startsWith('cloud_ai_'))
                  .map((x) => (
                    <span key={x} className="badge" style={{ marginLeft: 6 }}>
                      #{x}
                    </span>
                  ))}
                {ai?.caption && <div className="small">{ai.caption}</div>}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <AlarmSection homeId={ev.homeId} eventId={ev.id} />
      {dev?.isCamera && <ClipPlayer dev={dev} ts={ts} eventId={ev.id} lens={ev.lens ?? 0} parent={dev.parentAid ? all.find((d) => d.homeId === dev.homeId && d.aid === dev.parentAid) : undefined} />}
      <Related ev={ev} ts={ts} tree={tree} all={all} />
      {/* 開始與結束：用網址直接開也顯示（不靠 dev / 清單帶進來的資料；第八輪 B8-01） */}
      {/* 單筆端點沒給 phase 的舊後端也要配：一律問候選，這一筆不是開始 / 結束才不畫 */}
      {(api ?? dev?.apiEndpoint) && <CycleEvents api={(api ?? dev?.apiEndpoint)!} ev={ev} ts={ts} cycle={cycle} />}

      {/* 「原始欄位」沒有內容時不顯示（EVT-04） */}
      {Object.keys(ev.fields).length > 0 && (
        <details className="card" style={{ marginTop: 16 }} data-testid="evd-fields">
          <summary>{t('evd.fields')}</summary>
          <pre className="mono small" style={{ whiteSpace: 'pre-wrap' }}>
            {JSON.stringify(ev.fields, null, 2)}
          </pre>
        </details>
      )}
    </section>
  );
}

/** 警報處理（EVT-06）：狀態、按鈕、備註與歷程。不是警報（422 NOT_AN_ALARM）或還沒進索引（404）就不顯示。 */
function AlarmSection({ homeId, eventId }: { homeId: string; eventId: string }) {
  const { t } = useTranslation();
  const { session } = useApp();
  const me = session?.user?.user_id ?? '';
  const alarms = useAlarmStates();
  const api = alarms.apiOf(homeId);
  const shared = alarms.infoOf(eventId);
  const q = useQuery({
    queryKey: ['alarm', api, homeId, eventId, shared?.updated_at ?? ''],
    enabled: !!api,
    retry: false,
    queryFn: () => alarmApi.get(api!, homeId, eventId),
  });
  if (!q.data?.alarm?.state) return null;
  const history = Array.isArray(q.data.history) ? q.data.history : [];
  return (
    <div className="card" style={{ marginTop: 16 }} data-alarm-section={eventId}>
      <h2 className="section-title">{t('alarm.section')}</h2>
      <AlarmActions homeId={homeId} eventId={eventId} initial={q.data.alarm} />
      {!q.data.can_handle && <p className="muted small">{t('alarm.read_only')}</p>}
      <h3 className="small" style={{ marginTop: 12 }}>
        {t('alarm.history')}
      </h3>
      {history.length === 0 ? (
        <p className="muted small">{t('alarm.history_empty')}</p>
      ) : (
        <AlarmHistoryList history={history} me={me} />
      )}
    </div>
  );
}

/**
 * 前後片段：事件前 10 秒到後 20 秒（雲端）。打開頁面只查片段清單（唯讀、不記稽核）看那段時間有沒有雲端片段，
 * **按〔播放〕才建回放 session**（第九輪 R9-P1-3、B9-06：只打開詳細就在稽核留下「看了 … 的雲端錄影」）。
 * 按了還沒有畫面寫「載入中…」，8 秒還沒有寫「讀不到這段片段」＋〔在回放頁開啟〕（B9-08）。沒有片段就給回放頁入口。
 */
// 多鏡頭：片段、縮圖、回放 session 都用事件自己的鏡頭（SPEC #multi-lens-playback「有可播片段」只比對同一顆）
// autoStart：一打開就建回放 session 開始播（警報工作台按 Enter 開大圖 = 使用者明確要播，SPEC #vms-web-alarm-workbench）
export function ClipPlayer({ dev, ts, eventId, lens = 0, parent, autoStart = false }: { dev: VmsDevice; ts: number; eventId: string; lens?: number; parent?: VmsDevice; autoStart?: boolean }) {
  const { t, i18n } = useTranslation();
  const tzOf = useTzResolver();
  const tz = tzOf(dev.homeId);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [err, setErr] = useState<string | null>(null);
  // 不用原生控制列（它顯示片段自己的長度，片段異常時是「9:51:29」這種荒謬數字；第五輪 B5-01 / B5-04）：自己的播放鈕 + 真實時間
  const baseRef = useRef<number | null>(null);
  const [clock, setClock] = useState<number | null>(null);
  const [paused, setPaused] = useState(true);
  const [suspect, setSuspect] = useState(false);
  const [started, setStarted] = useState(autoStart);
  const [shown, setShown] = useState(false);
  const [slow, setSlow] = useState(false);
  const avail = useQuery({
    queryKey: ['evd-clip', dev.apiEndpoint, dev.homeId, dev.aid, ts, lens],
    queryFn: () => cloudClipIn(dev.apiEndpoint, dev.homeId, dev.aid, ts - 10, ts + 20, Date.now() / 1000, lens),
    staleTime: 60_000,
    retry: false,
  });
  const has = avail.data === 'ok' || avail.data === 'suspect' || avail.data === 'unknown';
  const poster = useThumb(dev.apiEndpoint, dev.homeId, dev.aid, ts * 1000, has && avail.data !== 'unknown', lens);
  // 這台的本機錄影（NVR / Kinio Station 的通道 = NVR 硬碟；有 SD 卡的攝影機 = SD 卡；都沒有 = 不給按鈕）
  const localKind = parent && isLocalRecorder(parent) ? 'nvr' : dev.storageKinds?.includes('sd') ? 'sd' : null;
  const isSuspect = suspect || avail.data === 'suspect';
  const toggle = (v: HTMLVideoElement) => {
    if (v.paused) void v.play().catch(() => {});
    else v.pause();
  };
  useEffect(() => {
    setStarted(autoStart);
    setShown(false);
    setSlow(false);
    setErr(null);
  }, [dev.apiEndpoint, dev.homeId, dev.aid, ts, autoStart]);
  useEffect(() => {
    if (!started) return;
    let hls: Hls | null = null;
    let canceled = false;
    const slowTimer = window.setTimeout(() => {
      const v = videoRef.current;
      if (!canceled && !(v && v.readyState >= 2)) setSlow(true);
    }, PLAY_FALLBACK_MS);
    (async () => {
      try {
        const ps = await vms.playbackSession(
          dev.apiEndpoint,
          dev.homeId,
          dev.aid,
          {
            source: 'cloud',
            from: new Date((ts - 10) * 1000).toISOString(),
            to: new Date((ts + 20) * 1000).toISOString(),
          },
          undefined,
          lens,
        );
        const url = (ps.playback as { playlist_url?: string } | undefined)?.playlist_url;
        if (!url) throw new Error('no_playlist');
        if (canceled || !videoRef.current) return;
        hls = new Hls({ enableWorker: true, maxBufferLength: 30 });
        const video = videoRef.current;
        hls.once(Hls.Events.LEVEL_LOADED, (_e, data) => {
          const pdt = data.details.fragments[0]?.programDateTime;
          const base = pdt ? pdt / 1000 : ts - 10;
          baseRef.current = base;
          if (clipSuspect(data.details.fragments, data.details.totalduration) || fragsSuspect(data.details.fragments, Date.now() / 1000)) setSuspect(true);
          // 播放清單第一段早於事件前 10 秒：跳到事件前 10 秒
          const off = ts - 10 - base;
          if (off > 1 && off < data.details.totalduration) video.currentTime = off;
        });
        hls.loadSource(url);
        hls.attachMedia(video);
        hls.on(Hls.Events.ERROR, (_e, d) => d.fatal && setErr('play'));
        void video.play().catch(() => {});
      } catch (e) {
        if (!canceled) setErr((e as { code?: string }).code ?? 'no_clip');
      }
    })();
    return () => {
      canceled = true;
      window.clearTimeout(slowTimer);
      hls?.destroy();
    };
  }, [started, dev.apiEndpoint, dev.homeId, dev.aid, ts, lens]);
  const openPb = (
    <Link className="btn btn-sm" to={playbackHref(dev.key, ts - 10, { event: eventId, lens })}>
      {t('evd.open_playback')}
    </Link>
  );
  return (
    <div className="card evd-clip" data-testid="evd-clip" data-state={avail.isLoading ? 'checking' : !has ? 'none' : !started ? 'idle' : err || slow ? 'failed' : shown ? 'playing' : 'loading'}>
      <h3>{t('evd.clip')}</h3>
      {/* 按鈕放在影片上方、影片最高 50vh：〔在回放頁開啟〕〔匯出這段〕不用捲動就看得到（第三輪 B3-26） */}
      <div className="row">
        {openPb}
        {/* 匯出這段：事件前 1 分鐘起 5 分鐘（網址帶攝影機與時段，SPEC #vms-web-540） */}
        {can(dev, 'recording.export') && (
          <Link className="btn btn-sm" to={exportHref(dev.key, ts - 60, 300)} data-testid="evd-export">
            {t('exports.export_this')}
          </Link>
        )}
      </div>
      {isSuspect && has && !err && (
        <p className="pb-notice small" role="status" data-testid="evd-clip-suspect">
          {t('evd.clip_suspect')}{' '}
          {/* 一鍵改看本機錄影（第六輪 R6-P0-1 ④）：回放頁指定本地來源（src=local），那一刻沒有就照回放頁的規則說明 */}
          {localKind && (
            <Link className="btn btn-sm" to={playbackHref(dev.key, ts - 10, { event: eventId, src: 'local' })} data-testid="evd-clip-local">
              {t('evd.clip_suspect_local', { src: t(`playback.src_${localKind}`) })}
            </Link>
          )}
        </p>
      )}
      {avail.isLoading ? (
        <p className="muted" data-testid="evd-clip-checking">
          {t('evd.clip_checking')}
        </p>
      ) : !has ? (
        <p className="muted" data-testid="evd-no-clip">
          {t('evd.no_cloud_clip')}
        </p>
      ) : err || slow ? (
        <p className="muted" role="status" data-testid="evd-clip-failed">
          {t('evd.clip_unreadable')} {openPb}
        </p>
      ) : (
        <>
          <video
            ref={videoRef}
            muted
            playsInline
            poster={poster ?? undefined}
            className="evd-video"
            onClick={(e) => (started ? toggle(e.currentTarget) : setStarted(true))}
            onPlay={() => setPaused(false)}
            onPause={() => setPaused(true)}
            onLoadedData={() => {
              setShown(true);
              setSlow(false);
            }}
            onTimeUpdate={(e) => {
              const at = (baseRef.current ?? ts - 10) + e.currentTarget.currentTime;
              setClock(at);
              // 片段異常時只播事件前後那一段（到事件後 20 秒停）
              if (isSuspect && at > ts + 20) e.currentTarget.pause();
            }}
            data-testid="evd-video"
          />
          <div className="row">
            <button
              className="btn btn-sm"
              onClick={() => (started ? videoRef.current && toggle(videoRef.current) : setStarted(true))}
              data-testid="evd-playpause"
            >
              {!started || paused ? t('playback.play') : t('playback.pause')}
            </button>
            {started && !shown && (
              <span className="muted small" role="status" data-testid="evd-clip-loading">
                {t('common.loading')}
              </span>
            )}
            {clock !== null && <span className="mono small">{formatTime(clock, tz, i18n.language, true)}</span>}
          </div>
        </>
      )}
    </div>
  );
}

/** 關聯攝影機同時刻（DEV-12 關聯）：事件來源是感測器時列出它關聯的攝影機，各給回放入口。 */
export function Related({ ev, ts, tree, all }: { ev: EventView; ts: number; tree: VmsDevice[]; all: VmsDevice[] }) {
  const { t } = useTranslation();
  const src = all.find((d) => d.homeId === ev.homeId && d.aid === ev.aid);
  const q = useQuery({
    queryKey: ['device-links', ev.homeId],
    queryFn: () => linkApi.list(src!.apiEndpoint, ev.homeId),
    enabled: !!src,
    retry: false,
  });
  const cams = (q.data?.data ?? [])
    .filter((l) => l.sensor_ref === ev.aid)
    .map((l) => all.find((d) => d.homeId === ev.homeId && d.aid === l.device_ref))
    .filter((d): d is VmsDevice => !!d);
  if (!cams.length) return null;
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3>{t('evd.related')}</h3>
      <ul className="plain-list">
        {cams.map((c) => (
          <li key={c.key}>
            {fullName(c, tree)}{' '}
            <Link className="btn btn-sm" to={playbackHref(c.key, ts - 5, { event: ev.id })}>
              {t('live.watch_playback')}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * 有 cycle_id 時：同一個 cycle 的最早開始、最晚結束（EVT-04）。沒有 cycle_id 的用 episodeSpan（同一個 device_ref＋binding_id＋key 配對）。
 * 只有瞬間事件（沒有開始 / 結束）= null（不畫）。
 */
export function cycleSpan(
  cur: { id: string; ts: number; phase?: string },
  others: { id: string; ts: number; phase?: string; cycle?: string }[],
  cycle: string,
): { start: number; end: number | null } | null {
  const all: { id: string; ts: number; phase?: string; cycle?: string }[] = [cur, ...others.filter((e) => e.id !== cur.id)];
  const mine = all.filter((e) => e === cur || e.cycle === cycle);
  const starts = mine.filter((e) => e.phase === 'started').map((e) => e.ts);
  const ends = mine.filter((e) => e.phase === 'ended').map((e) => e.ts);
  if (!starts.length && !ends.length) return null;
  const start = starts.length ? Math.min(...starts) : Math.min(...mine.map((e) => e.ts));
  return { start, end: ends.length ? Math.max(...ends) : null };
}

/**
 * 這一筆所在的那一次（第八輪 R8-P0-1、B8-01）：同一個 device_ref＋binding_id＋key，開始配之後第一個結束、結束配之前第一個開始，
 * 不跨過中間另一次。cur 沒有 phase 時用候選裡同一筆（同 id）的 phase。回傳 null = 這一筆不是開始 / 結束。
 */
export function episodeSpan(cur: EpItem, others: (EpItem & { alt?: string[] })[]): Episode | null {
  const same = others.find((o) => o.id === cur.id || o.alt?.includes(cur.id));
  const me: EpItem = { ...cur, phase: cur.phase ?? same?.phase, binding: cur.binding ?? same?.binding };
  if (!me.phase) return null;
  // 同一筆在候選裡的那一份不重複算（id 不同寫法、或兩個來源時間差幾秒）
  const rest = others.filter((o) => o !== same && o.id !== cur.id);
  // 候選沒有 binding_id 的照這一筆的（舊資料 / 串流）；有而且不同就是別的綁定
  const norm = rest.map((o) => ({ ...o, binding: o.binding ?? me.binding }));
  const ep = pairEpisodes([me, ...norm]).get(me.id);
  return ep ?? null;
}

/** 持續時間的白話：全站同一個函式（lib/duration，第十二輪 B12-07）。 */
export { durationText };

/** 開始後多久還沒有結束紀錄就不當「進行中」（10 分鐘）。 */
export const CYCLE_STALE_S = 600;
/** 配對候選：這一筆前後 15 分鐘 */
export const EPISODE_WINDOW_S = 900;

const isoS = (s: number) => new Date(s * 1000).toISOString().replace(/\.\d{3}Z$/, 'Z');

/**
 * 配對的候選（同一個來源，第八輪 B8-01：原本用 façade 的 ±30 分鐘 200 筆、沒照設備過濾，配到上一次）：
 * 全文檢索 device:<device_ref> 前後 15 分鐘（翻頁到 5 頁）；檢索不能用才退回 façade 並照 device_ref 過濾。
 */
async function episodeCandidates(api: string, ev: EventView, ts: number): Promise<(EpItem & { alt?: string[]; cycle?: string })[]> {
  const ref = ev.ref || ev.aid;
  try {
    const out: (EpItem & { alt?: string[]; cycle?: string })[] = [];
    let cursor: string | undefined;
    for (let i = 0; i < 5; i++) {
      const r = await searchApi.events(api, { q: `device:"${ref}" after:${isoS(ts - EPISODE_WINDOW_S)} before:${isoS(ts + EPISODE_WINDOW_S)}`, homes: [ev.homeId], cursor, limit: 200 });
      for (const d of r?.data ?? []) {
        if (d.key !== ev.key || (d.device_ref !== ref && d.device_ref !== ev.aid)) continue;
        out.push({ id: d.event_id, alt: [...idVariants(d.event_id)], ts: Date.parse(d.occurred_at), phase: phaseOf(d), ref: ev.aid, binding: d.binding_id || undefined, key: d.key, cycle: typeof d.payload?.cycle_id === 'string' ? (d.payload.cycle_id as string) : undefined });
      }
      cursor = r?.next_cursor;
      if (!cursor) break;
    }
    if (out.length) return out;
  } catch {
    /* 檢索不能用：退回 façade */
  }
  const r = await vms.events(api, ev.homeId, { device: ev.aid, from: isoS(ts - EPISODE_WINDOW_S), to: isoS(ts + EPISODE_WINDOW_S), limit: 200 });
  return (r?.data ?? [])
    .map((e) => fromFacade(ev.homeId, e))
    .filter((e) => e.aid === ev.aid && e.key === ev.key)
    .map((e) => ({ id: e.id, alt: [...idVariants(e.id)], ts: Date.parse(e.occurredAt), phase: e.phase as EpItem['phase'], ref: ev.aid, binding: e.binding, key: e.key, cycle: typeof e.fields.cycle_id === 'string' ? (e.fields.cycle_id as string) : undefined }));
}

function CycleEvents({ api, ev, ts, cycle }: { api: string; ev: EventView; ts: number; cycle: string }) {
  const { t, i18n } = useTranslation();
  const tzOf = useTzResolver();
  const q = useQuery({
    queryKey: ['evd-episode', ev.homeId, ev.aid, ev.id],
    queryFn: () => episodeCandidates(api, ev, ts),
    retry: false,
    staleTime: 30_000,
  });
  if (!q.data) return null;
  const tz = tzOf(ev.homeId);
  const name = eventName(t, ev.key, ev.cls, ev.fields, ev.ref || ev.aid);
  const fmt = (s: number) => formatTime(s, tz, i18n.language, true);
  // 有 cycle_id：照 cycle（同一個 cycle 的最早開始、最晚結束）
  let start: number | null = null;
  let end: number | null = null;
  let startId: string | undefined;
  let endOnly = false;
  if (cycle) {
    const span = cycleSpan({ id: ev.id, ts, phase: ev.phase }, q.data.filter((o) => o.cycle === cycle).map((o) => ({ id: o.id, ts: o.ts / 1000, phase: o.phase, cycle })), cycle);
    if (!span) return null;
    start = span.start;
    end = span.end;
  } else {
    const ep = episodeSpan({ id: ev.id, ts: ts * 1000, phase: ev.phase as EpItem['phase'], ref: ev.aid, binding: ev.binding, key: ev.key }, q.data);
    if (!ep) return null;
    const st = episodeState(ep);
    if (st.kind === 'end_only') endOnly = true;
    start = ep.start ? ep.start.ts / 1000 : null;
    end = ep.end ? ep.end.ts / 1000 : null;
    // 這一筆是結束：給開始那筆的入口
    if (ep.start && ep.end?.id === ev.id) startId = ep.start.id;
  }
  // 只有結束：開始不在前後 15 分鐘內（第八輪 B8-01：原本寫「還沒有結束」，它本身就是結束紀錄）
  if (endOnly || start === null)
    return (
      <div className="card" style={{ marginTop: 16 }} data-testid="evd-cycle" data-state="end_only">
        <h3>{t('evd.cycle')}</h3>
        <p style={{ margin: '0 0 8px' }} data-testid="evd-cycle-dur">
          {t('evd.cycle_end_only', { name })}
        </p>
        {end !== null && (
          <div className="row small muted">
            <span>
              {t('evd.phase_ended')} {fmt(end)}
            </span>
          </div>
        )}
      </div>
    );
  // 開始超過 10 分鐘還沒有結束紀錄：寫「沒有收到結束紀錄」，不畫「進行中」的進度條（第七輪 B7-14）
  const stale = end === null && Date.now() / 1000 - start > CYCLE_STALE_S;
  if (stale)
    return (
      <div className="card" style={{ marginTop: 16 }} data-testid="evd-cycle" data-state="stale">
        <h3>{t('evd.cycle')}</h3>
        <p style={{ margin: '0 0 8px' }} data-testid="evd-cycle-dur">
          {t('evd.cycle_no_end_record', { name })}
        </p>
        <div className="row small muted">
          <span>
            {t('evd.phase_started')} {fmt(start)}
          </span>
        </div>
      </div>
    );
  const len = Math.max(1, (end ?? Math.max(ts, start)) - start);
  const pos = Math.min(100, Math.max(0, ((ts - start) / len) * 100));
  return (
    <div className="card" style={{ marginTop: 16 }} data-testid="evd-cycle" data-state={end !== null ? 'done' : 'open'}>
      <h3>{t('evd.cycle')}</h3>
      <p style={{ margin: '0 0 8px' }} data-testid="evd-cycle-dur">
        {end !== null ? t('evd.cycle_dur', { name, dur: durationText(t, end - start) }) : t('evd.cycle_open', { name })}
      </p>
      <div className="evd-cycle-bar" aria-hidden>
        <span className="evd-cycle-fill" style={{ width: end !== null ? '100%' : '60%' }} />
        <span className="evd-cycle-mark" style={{ left: `${end !== null ? pos : Math.min(pos, 60)}%` }} title={t('evd.cycle_this')} />
      </div>
      <div className="row small muted" style={{ justifyContent: 'space-between' }}>
        <span data-testid="evd-cycle-start">
          {t('evd.phase_started')} {fmt(start)}
        </span>
        <span data-testid="evd-cycle-end">{end !== null ? `${t('evd.phase_ended')} ${fmt(end)}` : t('evd.cycle_no_end')}</span>
      </div>
      {/* 從結束那筆進來：給開始那筆的入口（第八輪 B8-01） */}
      {startId && (
        <p className="small" style={{ marginTop: 8 }}>
          <Link to={`/events/${encodeURIComponent(ev.homeId)}/${encodeURIComponent(startId)}`} data-testid="evd-cycle-open-start">
            {t('evd.cycle_open_start')}
          </Link>
        </p>
      )}
    </div>
  );
}
